import { useState, useRef, useEffect } from 'react';
import { Check, Funnel } from '@phosphor-icons/react';
import { type ReportStatus } from '../../../constants';

interface StatusFilterProps {
  statuses: ReportStatus[];
  selectedStatuses: ReportStatus[];
  onSelectionChange: (selected: ReportStatus[]) => void;
}

export const StatusFilter = ({
  statuses,
  selectedStatuses,
  onSelectionChange,
}: StatusFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const allSelected = selectedStatuses.length === statuses.length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleAll = () => {
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange([...statuses]);
    }
  };

  const handleToggleStatus = (status: ReportStatus) => {
    if (selectedStatuses.includes(status)) {
      const newSelection = selectedStatuses.filter((s) => s !== status);
      onSelectionChange(newSelection);
    } else {
      const newSelection = [...selectedStatuses, status];
      if (newSelection.length === statuses.length) {
        onSelectionChange([...statuses]);
      } else {
        onSelectionChange(newSelection);
      }
    }
  };

  const getDisplayText = () => {
    if (allSelected || selectedStatuses.length === 0) {
      return 'Status type';
    }
    if (selectedStatuses.length === 1) {
      return selectedStatuses[0];
    }
    return `${selectedStatuses.length} statuses`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Funnel
        size={18}
        aria-hidden="true"
        className="text-text-body pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
      />
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="border-ui-border text-body text-text-body focus:ring-action-link flex appearance-none items-center gap-2 rounded border bg-white py-2 pl-9 pr-3 focus:outline-none focus:ring-2"
      >
        {getDisplayText()}
      </button>

      {isOpen && (
        <div className="border-ui-border absolute left-0 top-full z-10 mt-1 max-h-64 w-64 overflow-y-auto rounded border bg-white shadow-lg">
          <label className="hover:bg-ui-background flex cursor-pointer items-center gap-2 border-b px-4 py-2">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={handleToggleAll}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-body font-semibold">All Status Types</span>
            {allSelected && (
              <Check
                size={16}
                aria-hidden="true"
                className="text-brand-primary ml-auto"
              />
            )}
          </label>

          {statuses.map((status) => (
            <label
              key={status}
              className="hover:bg-ui-background flex cursor-pointer items-center gap-2 px-4 py-2"
            >
              <input
                type="checkbox"
                checked={selectedStatuses.includes(status)}
                onChange={() => handleToggleStatus(status)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-body">{status}</span>
              {selectedStatuses.includes(status) && (
                <Check
                  size={16}
                  aria-hidden="true"
                  className="text-brand-primary ml-auto"
                />
              )}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};
